import { DateTime } from "luxon";
/* -----------------Globals--------------- */
import React, {
	createContext,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
	useMemo,
} from "react";

/* -----------------Child components--------------- */
import { trackCustomEvent, updateUserProperties } from "analytics";

/* -----------------Constants--------------- */
import { events } from "@constants/tracking";

import { isMockedData } from "@constants/demo";
import { getExamDaysLeft } from "@features/dashboard";
import { useProductDetails, useProductMetadata } from "@hooks/useProduct";
import useUserInfo from "@hooks/useUserInfo";
import { useAuthContext } from "@memorang/applets";
import {
	constructClientExamContext,
	retrieveExamContextFromKV,
	storeExamContextInKV,
} from "../helpers";
/* -----------------Local modules--------------- */
import updateExamMeta from "../mutations/UpdateExamMetadata";
import type { ExamDetails } from "../types";
import { defaultExamContext } from "./constants";

export type CurrentExamContext = {
	studyPackProductId: string;
	examName: string;
	examId: string;
	examDate?: string;
	examIcon?: string;
	studyPackProductNumericId: number;
	practiceTestProductId?: string;
	practiceTestProductNumericId?: number;
	hasRedeemed?: boolean;
	hasStudyPackAccess?: boolean;
	hasPracticeTestAccess?: boolean;
};

type ContextValue = {
	currentExamContext: CurrentExamContext;
	isExamContextLoading: boolean;
	updateCurrentExamContext: (examContext: CurrentExamContext) => void;
	updateExamDate: (examDate: Date) => void;
	handleSwitchExamContext: (examDetails: ExamDetails) => void;
};

const initExamContext = {
	studyPackProductId: "",
	examName: "",
	examId: "",
	studyPackProductNumericId: 0,
};

const Context = createContext<ContextValue>({
	currentExamContext: initExamContext,
	isExamContextLoading: true,
	updateCurrentExamContext: () => {
		//
	},
	updateExamDate: () => {
		//
	},
	handleSwitchExamContext: async () => {
		//
	},
});

const ExamContextProvider = ({ children }: { children: ReactNode }) => {
	const [currentExamContext, setCurrentExamContext] =
		useState<CurrentExamContext>(initExamContext);
	const [isExamContextLoading, setIsExamContextLoading] = useState(true);

	const { viewerId: userId } = useAuthContext();

	useProductDetails();
	useProductMetadata();
	useUserInfo();

	const updateCurrentExamContext = useCallback(
		(data: CurrentExamContext) => {
			setCurrentExamContext(data);
			if (userId) {
				storeExamContextInKV(data, userId);
			}
			const daysLeft = getExamDaysLeft(data.examDate);

			trackCustomEvent({
				eventName: events.examContextSwitched,
				exam_name: data.examName,
				exam_id: data.examId,
			});
			updateUserProperties({
				mem_exam_name: data.examName,
				mem_exam_id: data.examId,
				mem_exam_date: data.examDate!,
				mem_exam_interval: `${daysLeft} days`,
			});
		},
		[userId],
	);

	const syncExamContext = useCallback(async () => {
		setIsExamContextLoading(true);

		try {
			if (!userId) {
				if (isMockedData) {
					setCurrentExamContext(defaultExamContext);
				}
				return;
			}

			const cachedExamContext = await retrieveExamContextFromKV(userId);
			if (cachedExamContext) {
				const resolvedExamContext = isMockedData
					? {
							...defaultExamContext,
							...cachedExamContext,
							examDate: cachedExamContext.examDate,
						}
					: cachedExamContext;
				setCurrentExamContext(resolvedExamContext);
				return;
			}

			setCurrentExamContext(defaultExamContext);
			storeExamContextInKV(defaultExamContext, userId);
		} finally {
			setIsExamContextLoading(false);
		}
	}, [userId]);
	useEffect(() => {
		syncExamContext();
	}, [syncExamContext]);

	const updateExamDate = useCallback(
		(examDate: Date) => {
			if (examDate) {
				const formattedDate = DateTime.fromJSDate(examDate, {
					zone: "local",
				}).toFormat("MMMM dd, yyyy");
				const daysLeft = getExamDaysLeft(formattedDate);
				trackCustomEvent({
					eventName: events.examDateSet,
					exam_name: currentExamContext.examName,
					exam_id: currentExamContext.examId,
					exam_date: formattedDate,
					exam_interval: `${daysLeft} days`,
				});

				updateExamMeta({
					id: currentExamContext.examId,
					examDate: formattedDate,
				});
				updateCurrentExamContext({
					...currentExamContext,
					examDate: formattedDate,
				});
			}
		},
		[currentExamContext, updateCurrentExamContext],
	);

	const handleSwitchExamContext = useCallback(
		(examDetails: ExamDetails) => {
			const constructedExamContext = constructClientExamContext(examDetails);

			updateCurrentExamContext(constructedExamContext);
		},
		[updateCurrentExamContext],
	);

	const value = useMemo(() => {
		return {
			currentExamContext,
			isExamContextLoading,
			updateCurrentExamContext,
			updateExamDate,
			handleSwitchExamContext,
		};
	}, [
		currentExamContext,
		handleSwitchExamContext,
		isExamContextLoading,
		updateCurrentExamContext,
		updateExamDate,
	]);

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useExamContext = () => {
	const context = React.useContext(Context);
	if (context === undefined) {
		throw new Error("useExamContext must be used within a ExamContextProvider");
	}
	return context;
};

export { ExamContextProvider, useExamContext };
